import { AspError, maskSecrets } from "../utils/errors.js";
import type { ResolvedConfig } from "../config.js";
import {
  loadCredentials,
  isTokenExpired,
  type StoredCredentials,
} from "../auth/credentials.js";
import { refreshStoredSession, isSessionInvalidError } from "../auth/session.js";

export interface RequestOptions {
  method?: string;
  path: string;
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  prefix?: string;
  _retried?: boolean;
}

export class HttpClient {
  constructor(private readonly config: ResolvedConfig) {}

  async request<T>(opts: RequestOptions): Promise<T> {
    try {
      return await this.doRequest<T>(opts);
    } catch (err) {
      if (
        opts.auth !== false &&
        !opts._retried &&
        isSessionInvalidError(err) &&
        !this.config.token
      ) {
        const creds = await loadCredentials();
        if (creds?.refresh_token) {
          await refreshStoredSession(configWithPlatform(this.config, creds), creds.refresh_token, creds.platform_url);
          return this.doRequest<T>({ ...opts, _retried: true });
        }
      }
      throw err;
    }
  }

  private async doRequest<T>(opts: RequestOptions): Promise<T> {
    const creds = opts.auth !== false && !this.config.token ? await loadCredentials() : null;
    const platformUrl = creds?.platform_url ?? this.config.platformUrl;
    const prefix = opts.prefix ?? "";
    const url = `${platformUrl}${prefix}${opts.path}`;
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...opts.headers,
    };

    if (opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    if (opts.auth !== false) {
      const token = await this.resolveAccessToken(creds);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    if (this.config.verbose) {
      console.error(`[verbose] ${opts.method ?? "GET"} ${url}`);
      if (opts.body) console.error(maskSecrets(JSON.stringify(opts.body)));
    }

    const res = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });

    const text = await res.text();
    let data: unknown = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }

    if (this.config.verbose) {
      console.error(`[verbose] ${res.status}`, maskSecrets(text.slice(0, 2000)));
    }

    if (!res.ok) {
      throw AspError.fromResponse(res.status, data);
    }

    return data as T;
  }

  async resolveAccessToken(existing?: StoredCredentials | null): Promise<string | undefined> {
    if (this.config.token) return this.config.token;

    const creds = existing ?? (await loadCredentials());
    if (!creds) return undefined;

    if (isTokenExpired(creds) && creds.refresh_token) {
      const refreshed = await refreshStoredSession(
        configWithPlatform(this.config, creds),
        creds.refresh_token,
        creds.platform_url,
      );
      return refreshed.access_token;
    }

    return creds.access_token;
  }
}

function configWithPlatform(config: ResolvedConfig, creds: StoredCredentials): ResolvedConfig {
  return { ...config, platformUrl: creds.platform_url || config.platformUrl };
}
